import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2 } from "lucide-react";
import { useAuthContext } from "@/lib/auth/AuthContext";
import { loginRedirectPath } from "@/lib/auth/roles";
import { TurnstileField } from "@/components/auth/TurnstileField";

export function LoginPage() {
  const { signIn } = useAuthContext();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [turnstileToken, setTurnstileToken] = useState("");
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [loading, setLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  useEffect(() => {
    if (searchParams.get("verified") === "1") {
      setInfo("Email verified. You can sign in.");
    }
  }, [searchParams]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const session = await signIn(email, password, turnstileToken);
      const role = session?.user?.role;
      navigate(loginRedirectPath(role));
    } catch (err) {
      const msg = err.message || "Sign-in failed.";
      if (/invalid login credentials|not registered|incorrect password/i.test(msg)) {
        setError(
          "Email or password not recognized. Create a new account at Sign up, or use Forgot password."
        );
      } else if (/email not confirmed/i.test(msg)) {
        setError("Confirm your email first (check inbox/spam), then sign in again.");
      } else {
        setError(msg);
      }
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-24" data-testid="login-page">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="font-heading text-2xl text-center">Sign in</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={submit} className="space-y-4">
            <Input
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
            />
            <Input
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
            />
            <TurnstileField onToken={setTurnstileToken} />
            {error && <p className="text-sm text-destructive">{error}</p>}
            {info && <p className="text-sm text-muted-foreground">{info}</p>}
            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : "Sign in"}
            </Button>
          </form>
          <p className="text-center text-sm text-muted-foreground mt-6">
            <Link to="/forgot-password" className="text-primary">
              Forgot password?
            </Link>
            {" · "}
            <Link to="/signup" className="text-primary">
              Create account
            </Link>
          </p>
          <p className="text-center text-xs text-muted-foreground mt-4">
            Āchārya AI works without an account.{" "}
            <Link to="/guide" className="text-primary">
              Ask anonymously
            </Link>
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
